import {type ReactNode, useCallback, useState} from 'react'
import {Dialog, Flash, Textarea} from '@primer/react'
import {ModelPicker} from './ModelPicker'
import {SplitButton, type SplitButtonItem} from './SplitButton'
import styles from './CopilotFixDialog.module.css'

type ApplyChangesMode = 'commit' | 'pull_request'

export interface CopilotFixDialogSubmitOptions {
  createPullRequest: boolean
  instructions: string
  model: string
}

export interface CopilotFixDialogProps {
  title: string
  onClose: () => void
  onSubmit: (options: CopilotFixDialogSubmitOptions) => Promise<void | boolean>
  modelsUrl?: string
  submitDisabled?: boolean
  /** When false, the dialog will not auto-close after a successful onSubmit. Defaults to true. */
  closeOnSubmit?: boolean
  children?: ReactNode
}

const APPLY_MODE_ITEMS: Array<SplitButtonItem<ApplyChangesMode>> = [
  {
    value: 'commit',
    label: 'Fix and commit',
    description: "Copilot will push a commit directly to this pull request's branch.",
  },
  {
    value: 'pull_request',
    label: 'Fix and open pull request',
    description: 'Copilot will create a new pull request with the changes.',
  },
]

export const CopilotFixDialog: React.FC<CopilotFixDialogProps> = ({
  title,
  onClose,
  onSubmit,
  modelsUrl,
  submitDisabled = false,
  closeOnSubmit = true,
  children,
}) => {
  const [applyMode, setApplyMode] = useState<ApplyChangesMode>('commit')
  const [isFixing, setIsFixing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const [selectedModel, setSelectedModel] = useState('auto')

  const handleSubmit = useCallback(async () => {
    setIsFixing(true)
    setErrorMessage('')
    try {
      const result = await onSubmit({
        createPullRequest: applyMode === 'pull_request',
        instructions: additionalInstructions.trim(),
        model: selectedModel,
      })
      if (closeOnSubmit && result !== false) {
        onClose()
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.')
    } finally {
      setIsFixing(false)
    }
  }, [onSubmit, applyMode, additionalInstructions, selectedModel, closeOnSubmit, onClose])

  return (
    <Dialog onClose={onClose} title={title} width="xlarge">
      <Dialog.Body>
        {errorMessage && (
          <Flash variant="danger" className={styles.errorBanner} role="alert">
            {errorMessage}
          </Flash>
        )}
        <div>
          <p className="fgColor-muted">
            Fix the selected review comments with Copilot cloud agent. <b>monalisa</b> will be added as co-author on any
            new commits.
          </p>
        </div>
        <div className={styles.textareaContainer}>
          <Textarea
            className={styles.instructionsTextarea}
            aria-label="Additional instructions"
            placeholder="Add additional instructions..."
            value={additionalInstructions}
            onChange={e => setAdditionalInstructions(e.target.value)}
            rows={4}
            resize="none"
          />
          <div className={styles.textareaToolbar}>
            {modelsUrl && <ModelPicker modelsUrl={modelsUrl} onModelChange={setSelectedModel} />}
            <SplitButton
              items={APPLY_MODE_ITEMS}
              selectedValue={applyMode}
              onSelectionChange={setApplyMode}
              onAction={handleSubmit}
              variant="primary"
              size="small"
              disabled={isFixing || submitDisabled}
              loading={isFixing}
              dropdownAriaLabel="Change contribution method"
            />
          </div>
        </div>
        {children}
      </Dialog.Body>
    </Dialog>
  )
}
